import { ChangeDetectionStrategy, Component, ElementRef, HostListener, computed, inject, input, output, signal } from '@angular/core';

import { UiSelectOption } from './ui-select.types';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

@Component({
  selector: 'ui-select',
  standalone: true,
  imports: [UiLabelTranslatePipe],
  templateUrl: './ui-select.component.html',
  styleUrl: './ui-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiSelectComponent {
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  readonly label = input('');
  readonly id = input('');
  readonly value = input<string>('');
  readonly values = input<string[]>([]);
  readonly placeholder = input('Select option');
  readonly options = input<UiSelectOption[]>([]);
  readonly disabled = input(false);
  readonly required = input(false);
  readonly multiple = input(false);
  readonly error = input('');
  readonly hint = input('');
  readonly valueChange = output<string>();
  readonly valuesChange = output<string[]>();
  readonly isOpen = signal(false);
  readonly searchTerm = signal('');
  readonly selectedLabels = computed(() => {
    const selectedValues = new Set(this.values());
    return this.options()
      .filter((option) => selectedValues.has(option.value))
      .map((option) => option.label);
  });
  readonly visibleSelectedLabels = computed(() => this.selectedLabels().slice(0, 2));
  readonly hiddenSelectedCount = computed(() => Math.max(this.selectedLabels().length - 2, 0));
  readonly filteredOptions = computed(() => {
    const query = this.searchTerm().trim().toLowerCase();

    if (!query) {
      return this.options();
    }

    return this.options().filter((option) => option.label.toLowerCase().includes(query));
  });
  readonly hasSelectedValues = computed(() => this.selectedLabels().length > 0);
  readonly triggerAriaLabel = computed(() => {
    const labels = this.selectedLabels();

    if (!labels.length) {
      return this.placeholder();
    }

    return labels.join(', ');
  });

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: EventTarget | null): void {
    if (!target || this.elementRef.nativeElement.contains(target as Node)) {
      return;
    }

    this.isOpen.set(false);
    this.searchTerm.set('');
  }

  onValueChange(value: string): void {
    this.valueChange.emit(value);
  }

  toggleOpen(): void {
    if (this.disabled()) {
      return;
    }

    this.isOpen.update((isOpen) => {
      const nextOpen = !isOpen;

      if (!nextOpen) {
        this.searchTerm.set('');
      }

      return nextOpen;
    });
  }

  setSearchTerm(value: string): void {
    this.searchTerm.set(value);
  }

  toggleMultiValue(value: string): void {
    const selected = new Set(this.values());

    if (selected.has(value)) {
      selected.delete(value);
    } else {
      selected.add(value);
    }

    this.valuesChange.emit(Array.from(selected));
  }
}
